import { ClockIcon } from '@heroicons/react/24/outline';
import React from 'react';

interface StatusIndicatorProps {
  status: string;
  isScheduled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
  className?: string;
}

const STATUS_CONFIG = {
  scheduled: {
    color: 'text-purple-600',
    bg: 'bg-purple-100',
    ring: 'ring-purple-200',
    text: 'SCHED',
    pulse: true,
  },
  started: {
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    ring: 'ring-blue-200',
    text: 'START',
    pulse: true,
  },
  thinking: {
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    ring: 'ring-blue-200',
    text: 'THINK',
    pulse: true,
  },
  call_in_progress: {
    color: 'text-green-600',
    bg: 'bg-green-100',
    ring: 'ring-green-200',
    text: 'CALL',
    pulse: true,
  },
  waiting: {
    color: 'text-yellow-600',
    bg: 'bg-yellow-100',
    ring: 'ring-yellow-200',
    text: 'WAIT',
    pulse: false,
  },
  completed: {
    color: 'text-green-700',
    bg: 'bg-green-100',
    ring: 'ring-green-200',
    text: 'DONE',
    pulse: false,
  },
  failed: {
    color: 'text-red-600',
    bg: 'bg-red-100',
    ring: 'ring-red-200',
    text: 'FAIL',
    pulse: false,
  },
  cancelled: {
    color: 'text-gray-500',
    bg: 'bg-gray-50',
    ring: 'ring-gray-200',
    text: 'CANCELLED',
    pulse: false,
  },
  ineligible: {
    color: 'text-red-600',
    bg: 'bg-red-100',
    ring: 'ring-red-200',
    text: 'INELIG',
    pulse: false,
  },
  waiting_info: {
    color: 'text-yellow-600',
    bg: 'bg-yellow-100',
    ring: 'ring-yellow-200',
    text: 'WAIT',
    pulse: false,
  },
  dev_stalled: {
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    ring: 'ring-amber-200',
    text: 'ROUTE',
    pulse: true,
  },
  // Also handle the pending status for BlandCall
  pending: {
    color: 'text-gray-600',
    bg: 'bg-gray-100',
    ring: 'ring-gray-200',
    text: 'PEND',
    pulse: false,
  },
  success: {
    color: 'text-green-700',
    bg: 'bg-green-100',
    ring: 'ring-green-200',
    text: 'SUCCESS',
    pulse: false,
  },
  error: {
    color: 'text-red-600',
    bg: 'bg-red-100',
    ring: 'ring-red-200',
    text: 'ERROR',
    pulse: false,
  },
} as const;

const SIZE_CLASSES = {
  sm: 'px-1 py-0.5 text-xs',
  md: 'px-1.5 py-0.5 text-xs',
  lg: 'px-2 py-1 text-sm',
} as const;

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  isScheduled = false,
  size = 'sm',
  showText = true,
  className = '',
}) => {
  const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || {
    color: 'text-gray-600',
    bg: 'bg-gray-100',
    ring: 'ring-gray-200',
    text: 'UNK',
    pulse: false,
  };

  const sizeClass = SIZE_CLASSES[size];
  const pulseClass = config.pulse ? 'animate-pulse' : '';
  const ringClass = config.pulse ? `ring-1 ${config.ring}` : '';

  return (
    <span
      className={`
        ${config.color} ${config.bg} ${sizeClass} ${pulseClass} ${ringClass}
        rounded font-mono font-medium inline-flex items-center gap-1
        ${className}
      `}
      title={`Status: ${status}${isScheduled ? ' (Scheduled)' : ''}`}
    >
      {showText && config.text}
      {isScheduled && <ClockIcon className="h-2 w-2" />}
    </span>
  );
};
